// Onboarding for a signed-in clinician with no clinic membership. Two lanes:
//   'join'      → redeem an 8-char invite code (POST /clinic/v1/join)
//   'register'  → create a new clinic and become its admin
//                 (POST /clinic/v1/register)
//
// On success we invalidate the ['my-clinic'] query so ClinicGate re-renders
// into the app shell without a page reload. `refresh()` on the auth context
// picks up the clinician-row updates (full_name backfill from the create
// path) too.

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    CLINIC_FACILITY_TYPES,
    CLINIC_FACILITY_TYPE_LABELS,
    NIGERIAN_STATES,
    lgasForState,
    type ClinicFacilityType,
} from '@pamosi/shared';
import { clinicApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Logo } from '../components/Logo';

const PHONE_RE = /^\+[1-9]\d{7,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Lane = 'join' | 'register';

export function JoinClinicScreen() {
    const [lane, setLane] = useState<Lane>('join');
    const { clinician, signOut } = useAuth();
    return (
        <div className="min-h-full flex flex-col">
            <header className="px-6 py-4">
                <Logo size={32} />
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="pm-card w-full max-w-md p-6">
                    <div className="text-brand-700 text-xs uppercase tracking-wide font-semibold">
                        Pamosi Clinic
                    </div>
                    <h1 className="text-slate-900 text-xl font-semibold mt-1">
                        Set up your workspace
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Redeem an invite code from your clinic admin, or register
                        a new clinic to get started.
                    </p>

                    <div className="mt-5 grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1">
                        <button
                            type="button"
                            className={
                                'py-2 text-sm font-medium rounded-md transition ' +
                                (lane === 'join'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900')
                            }
                            onClick={() => setLane('join')}
                        >
                            Join a clinic
                        </button>
                        <button
                            type="button"
                            className={
                                'py-2 text-sm font-medium rounded-md transition ' +
                                (lane === 'register'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900')
                            }
                            onClick={() => setLane('register')}
                        >
                            Register a clinic
                        </button>
                    </div>

                    <div className="mt-5">
                        {lane === 'join' ? <JoinPane /> : <RegisterPane />}
                    </div>

                    <div className="mt-6 border-t border-slate-200 pt-4 flex items-center justify-between text-sm">
                        <div className="text-slate-500">
                            Signed in as {clinician?.phone_e164 ?? 'unknown'}
                        </div>
                        <button
                            type="button"
                            className="text-slate-600 hover:underline"
                            onClick={signOut}
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Join with invite code ──────────────────────────────────────────────

function JoinPane() {
    const qc = useQueryClient();
    const { refresh, clinician } = useAuth();
    // Only ask for full_name if the clinician row hasn't captured it yet —
    // avoids re-asking returning-with-invite users what they already gave.
    const needsFullName = !clinician?.full_name;
    const [code, setCode] = useState('');
    const [fullName, setFullName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        const normalized = code.trim().toUpperCase();
        // Invite codes are 8 alphanumeric chars per clinics module — enforce
        // that shape client-side so we don't POST obvious garbage.
        if (!/^[A-Z0-9]{8}$/.test(normalized)) {
            setError('Invite codes are 8 letters or digits.');
            return;
        }
        if (needsFullName && !fullName.trim()) {
            setError('Enter your full name.');
            return;
        }
        setBusy(true);
        try {
            await clinicApi.join({
                code: normalized,
                ...(needsFullName ? { fullName: fullName.trim() } : {}),
            });
            // Force a fresh /clinic/v1/me + /clinician/v1/me. ClinicGate
            // re-reads its query result and slides into the app shell.
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['my-clinic'] }),
                refresh(),
            ]);
        } catch (err) {
            setError(joinErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <form onSubmit={submit} className="space-y-4">
            {needsFullName && (
                <div>
                    <label className="pm-label" htmlFor="join-name">Your full name</label>
                    <input
                        id="join-name"
                        className="pm-input"
                        type="text"
                        autoComplete="name"
                        placeholder="e.g. Dr. Femi Adebayo"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        disabled={busy}
                    />
                </div>
            )}
            <div>
                <label className="pm-label" htmlFor="join-code">Invite code</label>
                <input
                    id="join-code"
                    className="pm-input tracking-widest uppercase"
                    type="text"
                    inputMode="text"
                    placeholder="e.g. ABC12345"
                    maxLength={8}
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={busy}
                />
                <div className="text-slate-500 text-xs mt-1">
                    Ask your clinic admin to share their 8-character invite code.
                </div>
            </div>
            {error && <div className="pm-error">{error}</div>}
            <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                {busy ? 'Joining…' : 'Join clinic'}
            </button>
        </form>
    );
}

function joinErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
        switch (err.code) {
            case 'invalid_or_expired_code':
            case 'invite_not_found': return 'That code is not valid. Double-check with your admin.';
            case 'invite_expired': return 'That invite code has expired. Ask your admin for a new one.';
            case 'invite_already_redeemed': return 'That code was already used.';
            case 'already_in_clinic':
            case 'already_member': return 'You are already part of a clinic.';
            case 'missing_full_name': return 'Enter your full name.';
        }
    }
    return 'Could not join. Try again.';
}

// ─── Register a new clinic ──────────────────────────────────────────────

function RegisterPane() {
    const qc = useQueryClient();
    const { refresh, clinician } = useAuth();
    const [fullName, setFullName] = useState(clinician?.full_name ?? '');
    const [adminEmail, setAdminEmail] = useState(clinician?.email ?? '');
    const [facilityName, setFacilityName] = useState('');
    const [facilityType, setFacilityType] = useState<ClinicFacilityType>('private_hospital');
    const [workspaceEmail, setWorkspaceEmail] = useState('');
    const [businessPhone, setBusinessPhone] = useState(clinician?.phone_e164 ?? '');
    const [physicalAddress, setPhysicalAddress] = useState('');
    const [country, setCountry] = useState('NG');
    const [stateName, setStateName] = useState('');
    const [lga, setLga] = useState('');
    const [license, setLicense] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const lgaOptions = useMemo(() => lgasForState(stateName), [stateName]);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        if (!fullName.trim()) return setError('Enter your full name.');
        if (!EMAIL_RE.test(adminEmail.trim())) return setError('Admin email is not valid.');
        if (!facilityName.trim()) return setError('Enter the clinic or facility name.');
        if (!EMAIL_RE.test(workspaceEmail.trim())) return setError('Workspace email is not valid.');
        if (!PHONE_RE.test(businessPhone.trim())) {
            return setError('Business phone must be in E.164 format (e.g. +2348012345678).');
        }
        if (!physicalAddress.trim()) return setError('Physical address is required.');
        if (!stateName) return setError('Select a state.');
        if (!lga) return setError('Select an LGA.');
        if (!license.trim()) return setError('License number is required.');
        setBusy(true);
        try {
            await clinicApi.register({
                fullName: fullName.trim(),
                adminEmail: adminEmail.trim(),
                facilityName: facilityName.trim(),
                facilityType,
                businessPhoneE164: businessPhone.trim(),
                licenseNumber: license.trim(),
                workspaceEmail: workspaceEmail.trim(),
                physicalAddress: physicalAddress.trim(),
                country: country.trim().toUpperCase(),
                state: stateName,
                lga,
            });
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['my-clinic'] }),
                refresh(),
            ]);
        } catch (err) {
            setError(registerErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <form onSubmit={submit} className="space-y-4">
            <div>
                <label className="pm-label" htmlFor="reg-name">Your full name</label>
                <input
                    id="reg-name"
                    className="pm-input"
                    type="text"
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={busy}
                />
            </div>
            <div>
                <label className="pm-label" htmlFor="reg-admin-email">Your email</label>
                <input
                    id="reg-admin-email"
                    className="pm-input"
                    type="email"
                    autoComplete="email"
                    placeholder="you@clinic.com"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    disabled={busy}
                />
            </div>
            <div>
                <label className="pm-label" htmlFor="reg-facility">Clinic / facility name</label>
                <input
                    id="reg-facility"
                    className="pm-input"
                    type="text"
                    placeholder="e.g. Adebayo Family Clinic"
                    value={facilityName}
                    onChange={(e) => setFacilityName(e.target.value)}
                    disabled={busy}
                />
            </div>
            <div>
                <label className="pm-label" htmlFor="reg-type">Facility type</label>
                <select
                    id="reg-type"
                    className="pm-input"
                    value={facilityType}
                    onChange={(e) => setFacilityType(e.target.value as ClinicFacilityType)}
                    disabled={busy}
                >
                    {CLINIC_FACILITY_TYPES.map((t) => (
                        <option key={t} value={t}>{CLINIC_FACILITY_TYPE_LABELS[t]}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="pm-label" htmlFor="reg-workspace-email">Workspace email</label>
                <input
                    id="reg-workspace-email"
                    className="pm-input"
                    type="email"
                    placeholder="info@facility.com"
                    value={workspaceEmail}
                    onChange={(e) => setWorkspaceEmail(e.target.value)}
                    disabled={busy}
                />
            </div>
            <div>
                <label className="pm-label" htmlFor="reg-phone">Business phone</label>
                <input
                    id="reg-phone"
                    className="pm-input"
                    type="tel"
                    placeholder="+2348012345678"
                    value={businessPhone}
                    onChange={(e) => setBusinessPhone(e.target.value)}
                    disabled={busy}
                />
            </div>
            <div>
                <label className="pm-label" htmlFor="reg-address">Physical address</label>
                <input
                    id="reg-address"
                    className="pm-input"
                    type="text"
                    placeholder="e.g. 45 Broad Street, Lagos Island"
                    value={physicalAddress}
                    onChange={(e) => setPhysicalAddress(e.target.value)}
                    disabled={busy}
                />
            </div>
            <div className="grid grid-cols-3 gap-3">
                <div>
                    <label className="pm-label" htmlFor="reg-country">Country</label>
                    <select
                        id="reg-country"
                        className="pm-input"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        disabled={busy}
                    >
                        <option value="NG">Nigeria</option>
                    </select>
                </div>
                <div>
                    <label className="pm-label" htmlFor="reg-state">State</label>
                    <select
                        id="reg-state"
                        className="pm-input"
                        value={stateName}
                        onChange={(e) => {
                            setStateName(e.target.value);
                            setLga('');
                        }}
                        disabled={busy}
                    >
                        <option value="">Select</option>
                        {NIGERIAN_STATES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="pm-label" htmlFor="reg-lga">LGA</label>
                    <select
                        id="reg-lga"
                        className="pm-input"
                        value={lga}
                        onChange={(e) => setLga(e.target.value)}
                        disabled={busy || !stateName}
                    >
                        <option value="">{stateName ? 'Select' : 'Pick state'}</option>
                        {lgaOptions.map((l) => (
                            <option key={l} value={l}>{l}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div>
                <label className="pm-label" htmlFor="reg-license">License number</label>
                <input
                    id="reg-license"
                    className="pm-input"
                    type="text"
                    placeholder="MDCN-…"
                    value={license}
                    onChange={(e) => setLicense(e.target.value)}
                    disabled={busy}
                />
            </div>
            {error && <div className="pm-error">{error}</div>}
            <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                {busy ? 'Registering…' : 'Register clinic'}
            </button>
            <div className="text-slate-500 text-xs">
                You’ll become the clinic admin. Verification usually completes
                within one business day; you can start using the workspace
                immediately.
            </div>
        </form>
    );
}

function registerErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
        switch (err.code) {
            case 'already_member':
            case 'already_in_clinic': return 'You are already part of a clinic.';
            case 'duplicate_cac': return 'A clinic with that CAC number is already registered.';
            case 'invalid_phone':
            case 'invalid_business_phone': return 'Business phone must be in E.164 format (e.g. +2348012345678).';
            case 'invalid_workspace_email': return 'Workspace email is not valid.';
            case 'invalid_admin_email': return 'Admin email is not valid.';
            case 'missing_physical_address': return 'Physical address is required.';
            case 'missing_state': return 'Select a state.';
            case 'missing_lga': return 'Select an LGA.';
            case 'invalid_country': return 'Select a country.';
            case 'missing_full_name': return 'Enter your full name.';
        }
    }
    return 'Could not register. Try again.';
}
